import { useLocation, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { useCurrentWorkspace } from '@/context/current-workspace';
import ReportingWebhooksPage from './index.page';
import NewReportingWebhookPage from './new.page';
import EditReportingWebhookPage from './edit.page';

const LOADING_LABEL = 'Loading reporting webhooks…';

/** Which of the three pages a route is, so both sides of the handoff can name the same one. */
export type ReportingWebhooksPageKind = 'index' | 'new' | 'edit';

type HandoffTarget = {
  kind: ReportingWebhooksPageKind;
  /** Only meaningful for `edit`. */
  webhookId?: string;
};

/**
 * The migrated URLs drop the `journeys` segment; the legacy ones are what the embedded Ember app
 * serves. Every page exists on both sides so either direction of the handoff can be resolved.
 */
function useHandoffHref(side: 'migrated' | 'legacy', { kind, webhookId }: HandoffTarget): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const params = { workspaceId, webhookId: webhookId ?? '' };
  const to =
    side === 'migrated'
      ? {
          index: '/workspaces/$workspaceId/integrations/reporting-webhooks',
          new: '/workspaces/$workspaceId/integrations/reporting-webhooks/new',
          edit: '/workspaces/$workspaceId/integrations/reporting-webhooks/$webhookId',
        }[kind]
      : {
          index: '/workspaces/$workspaceId/journeys/integrations/reporting-webhooks',
          new: '/workspaces/$workspaceId/journeys/integrations/reporting-webhooks/new',
          edit: '/workspaces/$workspaceId/journeys/integrations/reporting-webhooks/$webhookId',
        }[kind];

  const href = router.buildLocation({ to, params }).href;
  // Ember transitions preserve query params, so both directions carry the query string and hash.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

type JourneysReportingWebhooksHandoffProps = HandoffTarget;

/**
 * The old `/journeys/integrations/reporting-webhooks…` URLs. Flag off → the embedded Ember app
 * still owns the page and renders here. Flag on → Hydra has claimed the URL purely to forward it to
 * the migrated route, so Ember-side links keep working without every caller being re-pointed.
 */
export function JourneysReportingWebhooksHandoff(target: JourneysReportingWebhooksHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.WebhooksIntegrationMigration);
  const to = useHandoffHref('migrated', target);

  if (!isMigrated) {
    return <Journeys />;
  }

  // `/workspaces/last` is rewritten by a bridge-synced navigation that leaves route params stale,
  // so the id is read off the reactive location and held until it lands.
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type ReportingWebhooksRouteViewProps = HandoffTarget & {
  children: ReactNode;
};

/**
 * The migrated `/integrations/reporting-webhooks…` URLs.
 *
 * Flag on → Hydra owns the page, applying the same route-access gates Ember's `integrations` root
 * route did (reporting-webhooks nests under it, so it inherits both): Builder-plan accounts go to
 * the available-plans page, and accounts missing `journeys/imports/view` are redirected away.
 *
 * Flag off → these URLs have no Ember counterpart (Ember only knows the `journeys` form), so
 * rendering the embedded host here would 404 inside Ember. Hand back to the `journeys` twin
 * instead, which Ember does serve — the exact inverse of `JourneysReportingWebhooksHandoff`.
 */
function ReportingWebhooksRouteView({ children, ...target }: ReportingWebhooksRouteViewProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.WebhooksIntegrationMigration);
  const legacyHref = useHandoffHref('legacy', target);
  const workspace = useCurrentWorkspace();

  if (!isMigrated) {
    if (legacyHref == null) {
      return <PageLoader label={LOADING_LABEL} />;
    }
    return <SafeNavigate to={legacyHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  // Gate on the resolved workspace, not the URL segment: the pages' reads go through
  // `requireWorkspaceId`, which throws while the workspaces list is still loading and while the
  // `/workspaces/last` sentinel leaves route params stale.
  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId: String(workspace.id) })}
      >
        {children}
      </ProtectedRoute>
    </PlanGuard>
  );
}

export function ReportingWebhooksIndexRouteView() {
  return (
    <ReportingWebhooksRouteView kind="index">
      <ReportingWebhooksPage />
    </ReportingWebhooksRouteView>
  );
}

export function NewReportingWebhookRouteView() {
  return (
    <ReportingWebhooksRouteView kind="new">
      <NewReportingWebhookPage />
    </ReportingWebhooksRouteView>
  );
}

type EditReportingWebhookRouteViewProps = {
  webhookId: string;
};

export function EditReportingWebhookRouteView({ webhookId }: EditReportingWebhookRouteViewProps) {
  return (
    <ReportingWebhooksRouteView kind="edit" webhookId={webhookId}>
      <EditReportingWebhookPage webhookId={webhookId} />
    </ReportingWebhooksRouteView>
  );
}
